import { FC, useState } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { WalletIcon, UsersIcon, BuildingOffice2Icon, Cog6ToothIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { ArrowRightStartOnRectangleIcon, Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { Drawer, Button } from 'react-daisyui';
import Routes from '@/router/Routes.ts';
import { useAuthContext } from '@/context/AuthContext.tsx';
import { usePathname } from '@/hooks/usePathname.ts';
import { cn } from '@/utils/classNames.ts';

const navItems = [
  { label: 'Governance Events', path: Routes.ADMIN, icon: WalletIcon },
  { label: 'Governance Escalations', path: Routes.ADMIN_ESCALATIONS, icon: ExclamationTriangleIcon },
  { label: 'Decision Participants', path: Routes.ADMIN_USERS, icon: UsersIcon },
  { label: 'Operating Entities', path: Routes.ADMIN_ORGANIZATIONS, icon: BuildingOffice2Icon },
  { label: 'Account', path: Routes.ACCOUNT, icon: Cog6ToothIcon },
] as const;

const Logo: FC = () => <img src="/assets/logo1.svg" alt="COMMAND" className="w-[194px] max-w-full" />;

type NavProps = { onClick?: () => void };

const Nav: FC<NavProps> = ({ onClick }) => {
  const { logout } = useAuthContext();
  const pathname = usePathname();

  return (
    <nav className="flex flex-col justify-between grow">
      <div className="flex flex-col gap-3">
        {navItems.map(({ label, path, icon: Icon }) => {
          const isActive =
            path === Routes.ADMIN
              ? pathname === Routes.ADMIN || pathname.startsWith('/admin/sessions')
              : pathname.startsWith(path);
          return (
            <Link
              key={path}
              to={path}
              onClick={onClick}
              className={cn(
                'flex items-center gap-2 px-4 h-9 rounded-lg text-[#0f1b2a] text-sm font-dm transition-colors',
                isActive ? 'bg-[#d5e3fe] font-bold' : 'font-normal hover:bg-[#d5e3fe]/60'
              )}
            >
              <Icon className="w-[18px] h-[18px] shrink-0" />
              <span>{label}</span>
            </Link>
          );
        })}
      </div>

      <button
        onClick={logout}
        className="flex items-center gap-2 px-4 h-9 rounded-lg text-[#0f1b2a] text-sm font-medium font-dm hover:bg-[#d5e3fe]/60 transition-colors"
      >
        <ArrowRightStartOnRectangleIcon className="w-[18px] h-[18px] shrink-0" />
        <span>Logout</span>
      </button>
    </nav>
  );
};

const AdminLayout: FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <>
      <div className="flex min-h-[100dvh]">
        <aside className="flex flex-col gap-10 px-7 py-20 w-[250px] min-w-[250px] sticky top-0 h-screen overflow-hidden border-r border-[rgba(204,204,204,0.5)] bg-[#f4f9ff] hidden lg:flex">
          <div className="flex flex-col items-center px-4">
            <Link to={Routes.ADMIN}>
              <Logo />
            </Link>
          </div>
          <Nav />
        </aside>

        <div className="flex lg:hidden items-center gap-4 px-4 py-3 fixed top-0 left-0 right-0 z-10 bg-[#f4f9ff] border-b border-[rgba(204,204,204,0.5)]">
          <Link to={Routes.ADMIN_USERS}>
            <img src="/assets/logo1.svg" alt="COMMAND" className="w-[120px]" />
          </Link>
          <Button
            shape="circle"
            color="ghost"
            onClick={() => setIsMenuOpen((prev) => !prev)}
            className="p-2 ml-auto text-[#0f1b2a]"
          >
            <Bars3Icon className="w-6 h-6" />
          </Button>
        </div>

        <main className="flex flex-col flex-1 bg-white p-7 gap-8 min-h-screen overflow-auto mt-[52px] lg:mt-0">
          <Outlet />
        </main>
      </div>

      <Drawer
        className="z-10"
        end={false}
        side={
          <div className="flex flex-col w-[250px] h-[100dvh] overflow-auto py-10 px-7 gap-10 bg-[#f4f9ff] border-r border-[rgba(204,204,204,0.5)]">
            <div className="flex justify-end">
              <Button color="ghost" size="sm" className="text-[#0f1b2a]" onClick={() => setIsMenuOpen(false)}>
                <XMarkIcon className="w-5 h-5" />
              </Button>
            </div>
            <div className="flex flex-col items-center px-4">
              <Logo />
            </div>
            <Nav onClick={() => setIsMenuOpen(false)} />
          </div>
        }
        open={isMenuOpen}
        onClickOverlay={() => setIsMenuOpen(false)}
      />
    </>
  );
};

export default AdminLayout;
